import { httpCoreService } from "@/services/HttpCoreService";
import type {
  EnrollmentReportItem,
  FinancialSummaryReportItem,
  MonitoringReportItem,
  PaginatedReportResponse,
  ReportEndpointType,
  ReportExportParams,
  ReportsQueryParams,
  SaeSummaryReportItem,
  SitePerformanceReportItem,
} from "@/types/reports.type";
import type { ApiResponse } from "@/types/auth.type";

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
  if (!isSuccess) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

function buildParams(obj: Record<string, unknown>): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
      continue;
    }

    params.append(key, String(value));
  }

  return params.toString();
}

function withFeasibleSitesOnly<T extends ReportsQueryParams>(
  params: T,
): T & { feasibleSitesOnly: boolean } {
  return {
    ...params,
    feasibleSitesOnly: params.feasibleSitesOnly ?? true,
  };
}

export interface IReportsRepository {
  getEnrollmentReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<EnrollmentReportItem>>;
  getSitePerformanceReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SitePerformanceReportItem>>;
  getMonitoringReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<MonitoringReportItem>>;
  getSaeSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SaeSummaryReportItem>>;
  getFinancialSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<FinancialSummaryReportItem>>;
  exportReport(
    reportType: ReportEndpointType,
    params: ReportExportParams,
  ): Promise<Blob>;
}

export class ReportsRepository implements IReportsRepository {
  async getEnrollmentReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<EnrollmentReportItem>> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    const response = await httpCoreService.get<
      PaginatedReportResponse<EnrollmentReportItem>
    >(`/reports/enrollment?${query}`);
    assertSuccess(response, "Failed to fetch enrollment report");
    return response.data;
  }

  async getSitePerformanceReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SitePerformanceReportItem>> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    const response = await httpCoreService.get<
      PaginatedReportResponse<SitePerformanceReportItem>
    >(`/reports/site-performance?${query}`);
    assertSuccess(response, "Failed to fetch site performance report");
    return response.data;
  }

  async getMonitoringReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<MonitoringReportItem>> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    const response = await httpCoreService.get<
      PaginatedReportResponse<MonitoringReportItem>
    >(`/reports/monitoring?${query}`);
    assertSuccess(response, "Failed to fetch monitoring report");
    return response.data;
  }

  async getSaeSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<SaeSummaryReportItem>> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    const response = await httpCoreService.get<
      PaginatedReportResponse<SaeSummaryReportItem>
    >(`/reports/sae-summary?${query}`);
    assertSuccess(response, "Failed to fetch SAE summary report");
    return response.data;
  }

  async getFinancialSummaryReport(
    params: ReportsQueryParams,
  ): Promise<PaginatedReportResponse<FinancialSummaryReportItem>> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    const response = await httpCoreService.get<
      PaginatedReportResponse<FinancialSummaryReportItem>
    >(`/reports/financial-summary?${query}`);
    assertSuccess(response, "Failed to fetch financial summary report");
    return response.data;
  }

  async exportReport(
    reportType: ReportEndpointType,
    params: ReportExportParams,
  ): Promise<Blob> {
    const query = buildParams(
      withFeasibleSitesOnly(params) as unknown as Record<string, unknown>,
    );
    return httpCoreService.getBlob(
      `/reports/${reportType}/export${query ? `?${query}` : ""}`,
    );
  }
}
